import { Component, ElementRef, EventEmitter, inject, Input, Output, ViewChild } from '@angular/core';

import { Subscription } from 'rxjs';
import AppValues from 'src/app/common/app.values';
import { FormConstructorComponent } from 'src/app/forms/form-constructors/form-constructor/form-constructor.component';
import { FormStateService } from 'src/app/forms/form-state.service';
import {
  MapGotoBlockContent,
  MapGotoBlockForm,
  MapSearchBlockContent,
  MapSearchBlockForm,
  MapSearchBlockFormRaw,
} from 'src/app/interfaces/map.interface';
import { CustomMarker } from 'src/app/models/custom-marker.model';
import { MapFlags } from 'src/app/models/map-flags.model';
import { unsubscribeAll } from '../../../common/app.helpers';
import { MarkersStateChanges } from '../map.interfaces';
import { MapService } from '../map.service';
import { PinItemBindingMediator } from '../markers-handler-helpers/mediators/pin-item-binding.mediator';

@Component({
  selector: 'app-search-block',
  templateUrl: './search-block.component.html',
  styleUrls: ['./search-block.component.scss'],
})
export class SearchBlockComponent {
  @Input({ required: true }) public searchBlockContent: MapSearchBlockContent;
  @Input({ required: true }) public goToBlockContent: MapGotoBlockContent;
  @Input({ required: true }) public flags: MapFlags;
  @Input({ required: true }) public isAdminSession: boolean;
  @Input({ required: true }) public markers: CustomMarker[];
  @Input({ required: true }) public mediator: PinItemBindingMediator;

  @Output() public formSubmitted: EventEmitter<MapSearchBlockForm> = new EventEmitter<MapSearchBlockForm>();
  @Output() public goToSubmitted: EventEmitter<MapGotoBlockForm> = new EventEmitter<MapGotoBlockForm>();
  @Output() public closeSearchBlock: EventEmitter<MapSearchBlockFormRaw> = new EventEmitter<MapSearchBlockFormRaw>();
  @Output() public resetForm: EventEmitter<void> = new EventEmitter<void>();

  public mapMarkersTotal: number;

  public readonly DIGITS_REPRESENTATION_OPTIONS: string = AppValues.DIGITS_REPRESENTATION_OPTIONS;

  @ViewChild('element', { static: false }) private searchBlockElement: ElementRef<HTMLElement>;
  @ViewChild(FormConstructorComponent, { static: false }) private searchForm: FormConstructorComponent;

  private subscriptions$: Subscription[] = [];

  private formStateService = inject(FormStateService);
  private mapService = inject(MapService);

  public ngOnInit(): void {
    this.subscribeToMarkersChanges();
    this.recalculateMarkersTotal();

    this.mapService.resetFormTrigger.subscribe(() => {
      this.searchForm.childForm.resetForm();
    });
  }

  public onSubmitForm(event: Event): void {
    this.formStateService.stopAllAction(event);

    this.searchForm.prepareSubmitData();

    const formData = this.searchForm.childForm.formContent.data as MapSearchBlockFormRaw;

    const mapFormData: MapSearchBlockForm = {
      specialties: formData.specialties.map((s) => s.entity_id),
      specialtyTitles: formData.specialties.map((s) => s.title),
      diseases: formData.diseases.map((d) => d.entity_id),
      diseaseTitles: formData.diseases.map((d) => d.title),
      charity_focuses: formData?.charity_focuses?.map((c) => c.entity_id) ?? [],
      charityFocusesTitle: formData?.charity_focuses?.map((c) => c.title) ?? [],
      keyword: formData.keyword,
      roles: formData.roles,
    };

    this.formSubmitted.emit(mapFormData);
  }

  public onResetForm(event: Event): void {
    this.formStateService.stopAllAction(event);
    this.searchForm.childForm.resetForm();
    this.resetForm.emit();
  }

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions$);
  }

  public onCloseSearchBlock(event: Event): void {
    this.formStateService.stopAllAction(event);

    this.searchForm.prepareSubmitData();

    const formData = this.searchForm.childForm.formContent.data as MapSearchBlockFormRaw;

    this.closeSearchBlock.emit(formData);
  }

  public calculateMarkersTotal(markers: CustomMarker[]): number {
    return markers.reduce(
      (count: number, marker: CustomMarker) => count + (marker.label ? parseInt(marker.label.text, 10) : 0),
      0,
    );
  }

  public get elementRect(): DOMRect {
    return this.searchBlockElement.nativeElement.getBoundingClientRect();
  }

  /**
   * @description Recalculates markers total for search results. Needs if we close and open component to
   * recalculate mapMarkersTotal.
   */
  private recalculateMarkersTotal(): void {
    if (this.markers.length > 0) {
      this.mapMarkersTotal = this.calculateMarkersTotal(this.markers);
    }
  }

  /**
   * @description Note that totalMatchesCount is returned only for individual pins.
   */
  private subscribeToMarkersChanges(): void {
    this.subscriptions$.push(
      this.mapService.onMarkersChanges.subscribe((markersStateChanges: MarkersStateChanges) => {
        if (markersStateChanges.markers.totalMatchesCount) {
          this.mapMarkersTotal = markersStateChanges.markers.totalMatchesCount;
        } else if (markersStateChanges.markers.allMarkers) {
          this.mapMarkersTotal = this.calculateMarkersTotal(markersStateChanges.markers.allMarkers);
        }
      }),
    );
  }
}
